import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useOutletContext } from 'react-router-dom';
import { useEntityList } from '../../data/entities';
import {
  AreaChart, Donut, Heatmap, StatCard, DataTable, Sparkline,
  COLORS, STATUS_COLORS, fmtNum, fmtDur, fmtShortDate, fmtTime,
} from '../../components/charts';
import { askNova } from '../../components/ConsoleHeader';

// Athena · Insights — the org analytics dashboard. Aggregates live entities
// (workloads/pipelines/datasets per workspace, agents, review queue, usage
// counters, platform jobs) into the chart kit. A brand-new org renders every
// widget from clearly-labeled SAMPLE series so the module demos well; the
// moment real runs exist the widgets flip to live data.

const DAY = 24 * 60 * 60 * 1000;
const RANGES = [
  { key: '24h', label: '24h', ms: DAY, bucketMs: DAY / 24, fmt: fmtTime },
  { key: '7d', label: '7 days', ms: 7 * DAY, bucketMs: DAY, fmt: fmtShortDate },
  { key: '30d', label: '30 days', ms: 30 * DAY, bucketMs: DAY, fmt: fmtShortDate },
  { key: '90d', label: '90 days', ms: 91 * DAY, bucketMs: 7 * DAY, fmt: fmtShortDate },
];

// ---- per-workspace collector -------------------------------------------------
// Workloads/pipelines/datasets are workspace-scoped; hooks can't loop, so one
// invisible collector per workspace reports its docs up to the dashboard.
function WorkspaceCollector({ orgId, ws, onDocs }) {
  const scope = { org: orgId, workspace: ws.id };
  const { docs: workloads } = useEntityList('workload', scope);
  const { docs: pipelines } = useEntityList('pipeline', scope);
  const { docs: datasets } = useEntityList('dataset', scope);
  useEffect(() => { onDocs(ws.id, 'workloads', workloads || []); }, [ws.id, workloads, onDocs]);
  useEffect(() => { onDocs(ws.id, 'pipelines', pipelines || []); }, [ws.id, pipelines, onDocs]);
  useEffect(() => { onDocs(ws.id, 'datasets', datasets || []); }, [ws.id, datasets, onDocs]);
  return null;
}

// ---- sample series (deterministic, obviously synthetic shapes) ---------------
function sampleRuns(range) {
  const n = Math.round(range.ms / range.bucketMs);
  const t0 = Date.now() - range.ms;
  const runs = [];
  for (let i = 0; i < n; i += 1) {
    const wave = 4 + Math.round(3 * Math.sin(i / 2.1) + 2 * Math.sin(i / 5.7) + (i % 3));
    for (let k = 0; k < wave; k += 1) {
      const createdAt = t0 + i * range.bucketMs + (k / wave) * range.bucketMs;
      const roll = (i * 7 + k * 13) % 17;
      // The newest bucket keeps a couple of in-flight runs so the Active KPI
      // and status chips demo too.
      const running = i === n - 1 && k < 2;
      runs.push({
        id: `sample-${i}-${k}`,
        name: ['ingest-invoices', 'nightly-embed', 'score-leads', 'sync-crm', 'render-reports'][(i + k) % 5],
        kind: ['pipeline', 'workload', 'agent run'][(i + k) % 3],
        status: running ? 'running' : roll === 0 ? 'error' : roll === 1 ? 'stopped' : 'done',
        createdAt,
        startedAt: createdAt + 4000,
        finishedAt: running ? null : createdAt + 4000 + 30000 + ((i * 37 + k * 91) % 240000),
        sample: true,
      });
    }
  }
  return runs;
}

const SAMPLE_USAGE = [
  { label: 'ai tokens', value: 2841000 },
  { label: 'agent runs', value: 1274 },
  { label: 'pipeline docs', value: 8460 },
  { label: 'storage gb', value: 112 },
  { label: 'compute hrs', value: 64 },
];

function SampleBadge() {
  return <span className="ath-sample" title="Rendered from generated sample data — live runs replace this automatically.">sample</span>;
}

function StatusChip({ status }) {
  return (
    <span className="ck-chip" style={{ color: STATUS_COLORS[status] || 'var(--text)' }}>
      {status}
    </span>
  );
}

export default function AthenaModule() {
  const { org } = useOutletContext();
  const navigate = useNavigate();
  const [rangeKey, setRangeKey] = useState('7d');
  const range = RANGES.find((r) => r.key === rangeKey);

  // ---- live feeds (org-scoped only — platform jobs are NOT org data and
  // would defeat sample mode with out-of-range noise; they live in /jobs) ----
  const { docs: workspaces } = useEntityList('workspace', { org: org.id });
  const { docs: agents } = useEntityList('agent', { org: org.id });
  const { docs: reviewItems } = useEntityList('reviewItem', { org: org.id });
  const { docs: usageDocs } = useEntityList('usage', { org: org.id }, { orderBy: 'updatedAt', dir: 'desc' });
  // Outcomes (deck slide 13): conversations, resolution split, answer
  // quality, actions, exceptions — all live, per range.
  const { docs: sessions } = useEntityList('aiSession', { org: org.id });
  const { docs: testRuns } = useEntityList('testRun', { org: org.id });
  const { docs: commits } = useEntityList('commit', { org: org.id });
  const { docs: integrations } = useEntityList('integration', { org: org.id });
  const { docs: audit } = useEntityList('auditEvent', { org: org.id });
  const [wsDocs, setWsDocs] = useState({}); // `${wsId}:${kind}` -> docs
  const onDocs = React.useCallback((wsId, kind, docs) => {
    setWsDocs((m) => (m[`${wsId}:${kind}`] === docs ? m : { ...m, [`${wsId}:${kind}`]: docs }));
  }, []);

  const collect = (kind) => Object.entries(wsDocs)
    .filter(([k]) => k.endsWith(`:${kind}`))
    .flatMap(([k, docs]) => docs.map((d) => ({ ...d, _ws: k.split(':')[0] })));

  const workloads = collect('workloads');
  const pipelines = collect('pipelines');
  const datasets = collect('datasets');

  // ---- normalize runs (workloads + jobs share one event shape) ----
  const wsName = (id) => (workspaces || []).find((w) => w.id === id)?.name || id;
  // QA suite runs are real activity too — an org that has tested an app has
  // "run" something, so they flip the dashboard out of sample mode alongside
  // compute workloads.
  const realRuns = useMemo(() => [
    ...workloads.map((w) => ({
      id: `w-${w.id}`,
      name: w.name || w.id,
      kind: `workload · ${wsName(w._ws)}`,
      status: w.status === 'requested' || w.status === 'launching' ? 'running' : w.status,
      createdAt: w.createdAt || 0,
      startedAt: w.startedAt,
      finishedAt: w.finishedAt,
      link: `/orgs/${org.id}/compute`,
    })),
    ...(testRuns || []).map((r) => ({
      id: `qa-${r.id}`,
      name: r.summary ? `QA · ${r.summary.passed}/${r.summary.total} passed` : 'QA suite run',
      kind: 'qa run',
      status: r.status === 'passed' ? 'done' : ['failed', 'error'].includes(r.status) ? 'error' : 'running',
      createdAt: r.createdAt || 0,
      startedAt: r.createdAt,
      finishedAt: r.finishedAt,
      link: `/orgs/${org.id}/qa${r.appId ? `?app=${r.appId}` : ''}`,
    })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [workloads, testRuns, workspaces, org.id]);

  const sample = realRuns.length === 0;
  const allRuns = useMemo(
    () => (sample ? sampleRuns(range) : realRuns),
    [sample, realRuns, range],
  );

  const now = Date.now();
  const inRange = allRuns.filter((r) => r.createdAt >= now - range.ms);
  const inPrev = allRuns.filter((r) => r.createdAt >= now - 2 * range.ms && r.createdAt < now - range.ms);

  // ---- bucketed series ----
  const nBuckets = Math.round(range.ms / range.bucketMs);
  const bucketOf = (ts) => Math.min(nBuckets - 1, Math.max(0, Math.floor((ts - (now - range.ms)) / range.bucketMs)));
  const buckets = useMemo(() => {
    const mk = () => Array.from({ length: nBuckets }, () => 0);
    const done = mk(); const err = mk(); const total = mk();
    const durSum = mk(); const durN = mk();
    for (const r of inRange) {
      const b = bucketOf(r.createdAt);
      total[b] += 1;
      if (r.status === 'done') done[b] += 1;
      if (r.status === 'error') err[b] += 1;
      if (r.startedAt && r.finishedAt) { durSum[b] += r.finishedAt - r.startedAt; durN[b] += 1; }
    }
    const bx = (i) => now - range.ms + (i + 0.5) * range.bucketMs;
    return {
      x: Array.from({ length: nBuckets }, (_, i) => bx(i)),
      done, err, total,
      dur: durSum.map((s, i) => (durN[i] ? s / durN[i] : 0)),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inRange.length, rangeKey, sample]);

  // ---- KPI values ----
  const active = allRuns.filter((r) => r.status === 'running').length;
  const completed = inRange.filter((r) => r.status === 'done').length;
  const completedPrev = inPrev.filter((r) => r.status === 'done').length;
  const errored = inRange.filter((r) => r.status === 'error').length;
  const errRate = inRange.length ? Math.round((errored / inRange.length) * 100) : 0;
  const reviewPending = (reviewItems || []).filter((i) => !i.decision && i.status !== 'approved' && i.status !== 'rejected').length;

  // ---- outcomes (live counters) ----
  const since = now - range.ms;
  const inR = (docs, key = 'createdAt') => (docs || []).filter((d) => (d[key] || 0) >= since);
  const convs = inR(sessions);
  const reviews = inR(reviewItems);
  const reviewOpen = reviews.filter((r) => r.status === 'pending').length;
  const reviewHuman = reviews.filter((r) => r.status !== 'pending').length;
  const handoffs = reviews.filter((r) => ['handoff', 'low-confidence', 'missing-evidence'].includes(r.reason)).length;
  const resolvedAuto = Math.max(0, convs.length - handoffs);
  const runsR = inR(testRuns);
  const runsFailed = runsR.filter((r) => ['failed', 'error'].includes(r.status)).length;
  const integFailing = (integrations || []).filter((i) => i.lastTest && !i.lastTest.ok).length;
  const actions = inR(commits).length;
  const askEvents = inR(audit).filter((e) => e.action === 'review.handoff').length;
  const outcomes = [
    { label: 'Conversations', value: convs.length, color: COLORS.teal, hint: 'copilot + NoVA sessions' },
    { label: 'Resolved automatically', value: resolvedAuto, color: COLORS.green, hint: 'no human handoff' },
    { label: 'Human review', value: reviewHuman, color: COLORS.amber, hint: 'decided items' },
    { label: 'Open', value: reviewOpen, color: reviewOpen ? COLORS.red : COLORS.muted, hint: 'awaiting a decision' },
    { label: 'Actions committed', value: actions, color: COLORS.violet, hint: 'gated side effects' },
    { label: 'Exceptions', value: runsFailed + integFailing + askEvents, color: (runsFailed + integFailing) ? COLORS.red : COLORS.muted, hint: `${runsFailed} failed runs · ${integFailing} failing integrations · ${askEvents} handoffs` },
  ];
  const askAboutResults = () => askNova(`Summarize the last ${range.label} for org ${org.name}: ${convs.length} conversations, ${resolvedAuto} resolved automatically, ${reviewHuman} decided in Human Review, ${reviewOpen} open, ${actions} actions committed, ${runsFailed} failed QA runs, ${integFailing} failing integrations. What should we fix first?`);

  // ---- outcome donut ----
  const outcomeCounts = {};
  for (const r of inRange) outcomeCounts[r.status] = (outcomeCounts[r.status] || 0) + 1;
  const donutSlices = Object.entries(outcomeCounts)
    .map(([label, value]) => ({ label, value, color: STATUS_COLORS[label] || COLORS.violet }));

  // ---- heatmap (weekday x hour) ----
  const heat = Array.from({ length: 7 }, () => Array.from({ length: 24 }, () => 0));
  for (const r of inRange) {
    const d = new Date(r.createdAt);
    heat[(d.getDay() + 6) % 7][d.getHours()] += 1;
  }

  // ---- usage counters ----
  const usageCounters = (usageDocs || [])[0]?.counters || null;
  const usageData = usageCounters
    ? Object.entries(usageCounters).map(([label, value]) => ({ label, value: Number(value) || 0 }))
    : SAMPLE_USAGE;
  const usageSample = !usageCounters;

  // ---- workspace inventory rows ----
  const wsRows = (workspaces || []).map((w) => {
    const events = inRange.filter((r) => r.kind.endsWith(wsName(w.id)) || r._ws === w.id);
    const spark = Array.from({ length: Math.min(nBuckets, 30) }, () => 0);
    for (const e of events) spark[Math.min(spark.length - 1, Math.floor((bucketOf(e.createdAt) / nBuckets) * spark.length))] += 1;
    return {
      id: w.id,
      name: w.name || w.id,
      workloads: workloads.filter((x) => x._ws === w.id).length,
      pipelines: pipelines.filter((x) => x._ws === w.id).length,
      datasets: datasets.filter((x) => x._ws === w.id).length,
      spark,
    };
  });

  return (
    <div className="ath" data-nova-note={`Athena analytics, last ${range?.label || rangeKey}: conversations, resolution, answer quality, actions, exceptions and usage per workspace — read-only. NoVA explains the numbers (list_usage, list_orgEconomics) and opens the module a number points at.`.slice(0, 320)}>
      {(workspaces || []).map((ws) => (
        <WorkspaceCollector key={ws.id} orgId={org.id} ws={ws} onDocs={onDocs} />
      ))}

      <div className="ath-head">
        <div>
          <h1 style={{ margin: 0 }}>Athena <span className="pf-preview-tag">preview</span></h1>
          <p className="cs-help" style={{ marginTop: 6 }}>
            Live run activity, outcomes, and usage across this organization.
          </p>
          {sample && (
            <p className="cs-help ath-sample-note" style={{ marginTop: 6 }}>
              <SampleBadge /> Nothing has run yet, so every chart below is <strong>illustrative sample data</strong>.
              Your real numbers replace it the moment a pipeline, compute or QA run finishes.
            </p>
          )}
        </div>
        <div className="ath-range" role="tablist" aria-label="Time range">
          {RANGES.map((r) => (
            <button
              key={r.key}
              type="button"
              className={r.key === rangeKey ? 'on' : ''}
              onClick={() => setRangeKey(r.key)}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      <div className="cs-card ath-card" style={{ marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
          <h3 style={{ margin: 0 }}>Outcomes · {range.label}</h3>
          <span className="cs-help" style={{ fontSize: 12, flex: 1 }}>live — conversations, how they resolved, actions and exceptions</span>
          <button type="button" className="cs-btn cs-btn-outline cs-btn-sm" onClick={askAboutResults}>◈ Ask NoVA about results</button>
        </div>
        <div className="ath-kpis" style={{ marginTop: 10 }}>
          {outcomes.map((o) => <StatCard key={o.label} label={o.label} value={o.value} color={o.color} />)}
        </div>
        <p className="cs-help" style={{ marginTop: 8, fontSize: 12 }}>{outcomes.map((o) => `${o.label}: ${o.hint}`).join(' · ')}</p>
      </div>

      <div className="ath-kpis">
        <StatCard label="Active runs" value={active} color={COLORS.teal} spark={buckets.total.slice(-24)} />
        <StatCard label={`Completed · ${range.label}`} value={completed} delta={completed - completedPrev} color={COLORS.green} spark={buckets.done.slice(-24)} />
        <StatCard label="Error rate" value={errRate} display={`${errRate}%`} delta={null} color={errRate > 10 ? COLORS.red : COLORS.violet} spark={buckets.err.slice(-24)} />
        <StatCard label="Agents" value={(agents || []).length} color={COLORS.sky} />
        <StatCard label="Datasets" value={datasets.length} color={COLORS.amber} />
        <StatCard label="Review queue" value={reviewPending} color={reviewPending ? COLORS.amber : COLORS.muted} />
      </div>

      <div className="ath-grid">
        <div className="cs-card ath-card ath-span8">
          <h3>Runs over time {sample && <SampleBadge />}</h3>
          <AreaChart
            height={210}
            formatX={range.fmt}
            series={[
              { name: 'completed', color: COLORS.teal, points: buckets.x.map((x, i) => ({ x, y: buckets.done[i] })) },
              { name: 'errored', color: COLORS.red, points: buckets.x.map((x, i) => ({ x, y: buckets.err[i] })) },
            ]}
          />
        </div>
        <div className="cs-card ath-card ath-span4">
          <h3>Outcomes · {range.label} {sample && <SampleBadge />}</h3>
          <Donut slices={donutSlices} centerLabel="runs" centerValue={inRange.length} />
        </div>
        <div className="cs-card ath-card ath-span5">
          <h3>Avg run duration {sample && <SampleBadge />}</h3>
          <AreaChart
            height={180}
            formatX={range.fmt}
            formatY={fmtDur}
            series={[{ name: 'duration', color: COLORS.violet, points: buckets.x.map((x, i) => ({ x, y: buckets.dur[i] })) }]}
          />
        </div>
        <div className="cs-card ath-card ath-span7">
          <h3>Activity heatmap · {range.label} {sample && <SampleBadge />}</h3>
          <Heatmap grid={heat} />
        </div>
        <div className="cs-card ath-card ath-span12">
          <h3>Usage counters {usageSample && <SampleBadge />}</h3>
          {/* Meters span wildly different scales (millions of tokens vs tens
              of compute hours) — one shared bar axis flattens everything, so
              each meter gets its own stat instead. */}
          <div className="ath-usage">
            {usageData.map((u) => (
              <div key={u.label} className="ath-usage-item">
                <strong>{fmtNum(u.value)}</strong>
                <span>{u.label}</span>
              </div>
            ))}
          </div>
          <p className="cs-help" style={{ marginTop: 10, fontSize: 12 }}>
            Month-to-date meters{usageSample ? ' (sample)' : ''} — billing detail in{' '}
            <Link to={`/orgs/${org.id}/billing`} style={{ color: 'var(--surf)' }}>Billing</Link>.
          </p>
        </div>
      </div>

      <div className="cs-card ath-card" style={{ marginTop: 14 }}>
        <h3>Recent runs {sample && <SampleBadge />}</h3>
        <DataTable
          rows={[...inRange].sort((a, b) => b.createdAt - a.createdAt)}
          rowKey={(r) => r.id}
          filterKeys={['name', 'kind', 'status']}
          filterPlaceholder="Filter runs…"
          defaultSort={{ key: 'started', dir: 'desc' }}
          limit={10}
          onRowClick={sample ? null : (r) => r.link && navigate(r.link)}
          columns={[
            { key: 'name', label: 'Run', render: (r) => <strong>{r.name}</strong> },
            { key: 'kind', label: 'Kind' },
            { key: 'status', label: 'Status', render: (r) => <StatusChip status={r.status} /> },
            {
              key: 'duration',
              label: 'Duration',
              align: 'right',
              sortValue: (r) => (r.startedAt && r.finishedAt ? r.finishedAt - r.startedAt : -1),
              render: (r) => (r.startedAt && r.finishedAt ? fmtDur(r.finishedAt - r.startedAt) : '—'),
            },
            {
              key: 'started',
              label: 'Started',
              align: 'right',
              sortValue: (r) => r.createdAt,
              render: (r) => `${fmtShortDate(r.createdAt)} ${fmtTime(r.createdAt)}`,
            },
          ]}
          empty="No runs in this range."
        />
        <p className="cs-help" style={{ marginTop: 8, fontSize: 13 }}>
          Run control (cancel / pause / resume) lives in <Link to="/jobs" style={{ color: 'var(--surf)' }}>Processes</Link>.
        </p>
      </div>

      {wsRows.length > 0 && (
        <div className="cs-card ath-card" style={{ marginTop: 14 }}>
          <h3>Workspaces</h3>
          <DataTable
            rows={wsRows}
            rowKey={(r) => r.id}
            defaultSort={{ key: 'workloads', dir: 'desc' }}
            onRowClick={(r) => navigate(`/orgs/${org.id}/ws/${r.id}`)}
            columns={[
              { key: 'name', label: 'Workspace', render: (r) => <strong>{r.name}</strong> },
              { key: 'workloads', label: 'Workloads', align: 'right' },
              { key: 'pipelines', label: 'Pipelines', align: 'right' },
              { key: 'datasets', label: 'Datasets', align: 'right' },
              {
                key: 'activity',
                label: `Activity · ${range.label}`,
                align: 'right',
                sortValue: (r) => r.spark.reduce((a, b) => a + b, 0),
                render: (r) => <Sparkline points={r.spark} width={110} height={22} />,
              },
            ]}
          />
        </div>
      )}
    </div>
  );
}
